import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import {
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Box,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	MenuItem,
	Select,
	FormControl,
	InputLabel,
	Tooltip,
	IconButton,
} from "@mui/material";
import { useDispatch, useSelector } from "react-redux";

import PATCloseButton from "../../components/Buttons/PATCloseButton";
import PATSaveButton from "../../components/Buttons/PATSaveButton";
import PATPrintButton from "../../components/Buttons/PATPrintButton";
import TextFieldController from "../../components/inputs/TextFieldController";
import { maxLengthMessage } from "../../utils/functions";
import moment from "moment";
import { AssignmentReturn } from "@mui/icons-material";
import { entityNamesForCustomId, serviceNames } from "../../utils/constants";
import { getService } from "../../reducers/service";
import { notify } from "../../reducers/NotifierReducer";
import { RegularExpressions } from "../../utils/regular-expressions";

const dispensableItemSchema = yup.object().shape({
	dispensableItemNum: yup
		.string()
		.trim()
		.matches(RegularExpressions.ID, "Invalid Dispensable Item#")
		.max(30, maxLengthMessage(30)),
	trackingNumber: yup
		.string()
		.trim()
		.max(50, maxLengthMessage(50))
		.required("Tracking Number is required"),
	vendor: yup
		.string()
		.trim()
		.max(100, maxLengthMessage(100))
		.required("Vendor is required"),
	description: yup.string().trim().max(500, maxLengthMessage(500)).nullable(),
	cost: yup
		.number()
		.required("Cost is required")
		.min(0, "Cost must be 0 or greater")
		.transform((v, o) => (o === "" ? null : v)),
	unitsInStock: yup
		.number()
		.required("Units in Stock is required")
		.min(0, "Units must be 0 or greater")
		.transform((v, o) => (o === "" ? null : v)),
	maxStock: yup
		.number()
		.required("Max Stock is required")
		.min(0, "Max stock must be 0 or greater")
		.transform((v, o) => (o === "" ? null : v)),
	minBeforeRestock: yup
		.number()
		.required("Minimum Before Restock is required")
		.min(0, "Minimum must be 0 or greater")
		.transform((v, o) => (o === "" ? null : v)),
	notes: yup.string().trim().max(500, maxLengthMessage(500)).nullable(),
});

const buildDefaults = (initialData) => ({
	dispensableItemNum: initialData?.dispensableItemNum || "",
	trackingNumber: initialData?.trackingNumber || "",
	vendor: initialData?.vendor || "",
	description: initialData?.description || "",
	cost: initialData?.cost ?? "",
	unitsInStock: initialData?.unitsInStock ?? "",
	maxStock: initialData?.maxStock ?? "",
	minBeforeRestock: initialData?.minBeforeRestock ?? "",
	notes: initialData?.notes || "",
});

const fmtDate = (dateStr) => {
	if (!dateStr) return "—";
	return moment(dateStr).format("MM/DD/YYYY");
};

const AddEditDispensableItemDialog = ({
	open,
	onClose,
	onSave,
	initialData,
	viewOnly = false,
	onPrint,
	employees = [],
	vehicles = [],
}) => {
	const dispatch = useDispatch();
	const userAgency = useSelector((state) => state.user.userAgency);
	const user = useSelector((state) => state.user);
	const [issHistoryLimit, setIssHistoryLimit] = useState(10);

	const {
		control,
		handleSubmit,
		reset,
		setValue,
		formState: { errors, isValid },
	} = useForm({
		resolver: yupResolver(dispensableItemSchema),
		defaultValues: buildDefaults(initialData),
	});

	useEffect(() => {
		reset(buildDefaults(initialData));
		setIssHistoryLimit(10);
	}, [initialData, reset]);

	useEffect(() => {
		if (!open) {
			reset();
			setIssHistoryLimit(10);
		}
	}, [open, reset]);

	const onSubmit = async (data) => {
		let dispensableItemGeneratedId;
		if (!data.dispensableItemNum?.trim()) {
			dispensableItemGeneratedId = await generateCustomId();
		}
		const payload = {
			dispensableItemNum: dispensableItemGeneratedId ? dispensableItemGeneratedId : data?.dispensableItemNum?.trim() || null,
			trackingNumber: data.trackingNumber?.trim() || null,
			vendor: data.vendor?.trim() || null,
			description: data.description?.trim() || null,
			cost: data.cost ?? null,
			unitsInStock: data.unitsInStock ?? null,
			maxStock: data.maxStock ?? null,
			minBeforeRestock: data.minBeforeRestock ?? null,
			notes: data.notes?.trim() || null,
			agencyId: user?.userAgency || null,
		};

		onSave(payload);
		reset();
		onClose();
	};

	const dialogTitle = initialData
		? viewOnly
			? `View — ${initialData.trackingNumber}`
			: `Edit — ${initialData.trackingNumber}`
		: "Add Dispensable Item";

	const issuances = initialData?.issuances || [];
	const sortedIssuances = [...issuances]
		.sort(
			(a, b) => (b.date || "").localeCompare(a.date || "") || b.id - a.id,
		)
		.slice(0, issHistoryLimit === "all" ? undefined : issHistoryLimit);

	const generateCustomId = async () => {
		const data = {
			EntityName: entityNamesForCustomId.DispensableId,
			agencyID: userAgency,
		};
		const service = getService(serviceNames.customId);
		service.timeout = 20000;

		const id = await service.create(data);
		if (id?.includes("Agency ID does not match")) {
			dispatch(
				notify(
					"Unable to generate Dispensable Item ID because the Agency ID does not match.",
					"warning",
				),
			);
			return null;
		}
		return id;
	};

	useEffect(() => {
		setValue("dispensableItemNum", "");
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [userAgency]);

	const handleAutoGenerateClick = async () => {
		const id = await generateCustomId();
		setValue("dispensableItemNum", id, { shouldValidate: true });
	};

	return (
		<Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
			<DialogTitle>{dialogTitle}</DialogTitle>

			<DialogContent
				sx={{
					pointerEvents: viewOnly ? "none" : undefined,
					opacity: viewOnly ? 0.75 : undefined,
				}}
			>
				<Box
					component="form"
					onSubmit={handleSubmit(onSubmit)}
					noValidate
				>
					<Box
						sx={{
							display: "grid",
							gridTemplateColumns: "repeat(2, 1fr)",
							gap: 2,
							pt: 1,
						}}
					>
						<Box display="flex" alignItems="center">
							<Box flexGrow={1}>
								<TextFieldController
									name="dispensableItemNum"
									label="Dispensable Item#"
									required={true}
									control={control}
									errors={errors}
								/>
							</Box>
							<Box>
								<Tooltip title="Auto generate dispensable item number">
									<IconButton
										color="primary"
										aria-label="Auto generate dispensable item number"
										size="large"
										onClick={handleAutoGenerateClick}
									>
										<AssignmentReturn />
									</IconButton>
								</Tooltip>
							</Box>
						</Box>
						<Box>
							<TextFieldController
								name="trackingNumber"
								label="Tracking Number*"
								control={control}
								errors={errors}
								disabled={!!initialData}
							/>
						</Box>
						<Box>
							<TextFieldController
								name="vendor"
								label="Vendor*"
								control={control}
								errors={errors}
								placeholder="Vendor name…"
							/>
						</Box>
						<Box sx={{ gridColumn: "1 / -1" }}>
							<TextFieldController
								name="description"
								label="Description"
								control={control}
								errors={errors}
								placeholder="Item description…"
							/>
						</Box>
						<Box>
							<TextFieldController
								name="cost"
								label="Unit Cost ($)*"
								control={control}
								errors={errors}
								type="number"
								placeholder="0.00"
							/>
						</Box>
						<Box>
							<TextFieldController
								name="unitsInStock"
								label="Units in Stock*"
								control={control}
								errors={errors}
								type="number"
								placeholder="0"
							/>
						</Box>
						<Box>
							<TextFieldController
								name="maxStock"
								label="Maximum to Stock*"
								control={control}
								errors={errors}
								type="number"
								placeholder="Optional"
							/>
						</Box>
						<Box>
							<TextFieldController
								name="minBeforeRestock"
								label="Minimum Before Restock*"
								control={control}
								errors={errors}
								type="number"
								placeholder="0"
							/>
						</Box>
						<Box sx={{ gridColumn: "1 / -1" }}>
							<TextFieldController
								name="notes"
								label="Notes"
								control={control}
								errors={errors}
								multiline
								rows={2}
								placeholder="Additional notes…"
							/>
						</Box>
					</Box>

					{/* Issuance History */}
					{initialData && issuances.length > 0 && (
						<Box sx={{ mt: 3 }}>
							<Box
								sx={{
									display: "flex",
									alignItems: "center",
									justifyContent: "space-between",
									mb: 1,
								}}
							>
								<Typography
									variant="caption"
									fontWeight={700}
									color="text.secondary"
									sx={{
										textTransform: "uppercase",
										letterSpacing: "0.06em",
									}}
								>
									Issuance History ({issuances.length})
								</Typography>
								<FormControl
									size="small"
									sx={{ minWidth: 100 }}
								>
									<InputLabel
										sx={{
											fontSize: 12,
											backgroundColor: "transparent",
										}}
									>
										Limit
									</InputLabel>
									<Select
										label="Limit"
										value={issHistoryLimit}
										onChange={(e) =>
											setIssHistoryLimit(e.target.value)
										}
										sx={{ fontSize: 12, height: 30 }}
									>
										<MenuItem value={10}>Last 10</MenuItem>
										<MenuItem value={25}>Last 25</MenuItem>
										<MenuItem value={50}>Last 50</MenuItem>
										<MenuItem value="all">All</MenuItem>
									</Select>
								</FormControl>
							</Box>

							<TableContainer
								sx={{
									border: "1px solid",
									borderColor: "divider",
									borderRadius: 1,
								}}
							>
								<Table size="small">
									<TableHead>
										<TableRow
											sx={{
												backgroundColor: "action.hover",
											}}
										>
											<TableCell
												sx={{
													fontWeight: 700,
													fontSize: 11,
												}}
											>
												Date
											</TableCell>
											<TableCell
												sx={{
													fontWeight: 700,
													fontSize: 11,
												}}
											>
												Employee / Type
											</TableCell>
											<TableCell
												sx={{
													fontWeight: 700,
													fontSize: 11,
												}}
											>
												Vehicle
											</TableCell>
											<TableCell
												sx={{
													fontWeight: 700,
													fontSize: 11,
												}}
											>
												Serial / ID
											</TableCell>
											<TableCell
												align="right"
												sx={{
													fontWeight: 700,
													fontSize: 11,
												}}
											>
												Qty
											</TableCell>
											<TableCell
												align="right"
												sx={{
													fontWeight: 700,
													fontSize: 11,
												}}
											>
												Total
											</TableCell>
											<TableCell
												sx={{
													fontWeight: 700,
													fontSize: 11,
												}}
											>
												Comments
											</TableCell>
										</TableRow>
									</TableHead>
									<TableBody>
										{sortedIssuances.map((iss) => {
											const emp = employees.find(
												(e) =>
													e.value ===
													String(iss.perPersonnelId),
											);
											const veh = vehicles.find(
												(v) => v.id === iss.vehicleId,
											);
											return (
												<TableRow
													key={iss.id}
													sx={{
														backgroundColor:
															iss.isRestock
																? "rgba(99,102,241,0.04)"
																: undefined,
													}}
												>
													<TableCell
														sx={{ fontSize: 11 }}
													>
														{fmtDate(iss.date)}
													</TableCell>
													<TableCell
														sx={{
															fontSize: 11,
															color: iss.isRestock
																? "primary.main"
																: undefined,
														}}
													>
														{iss.isRestock
															? "Restock"
															: emp
																? `${emp.label}`
																: "—"}
													</TableCell>
													<TableCell
														sx={{ fontSize: 11 }}
													>
														{veh
															? veh.unitNumber
															: "—"}
													</TableCell>
													<TableCell
														sx={{
															fontSize: 11,
															fontFamily:
																"monospace",
															color: "text.secondary",
														}}
													>
														{iss.serialId || "—"}
													</TableCell>
													<TableCell
														align="right"
														sx={{
															fontSize: 11,
															fontWeight: 600,
															color: iss.isRestock
																? "success.main"
																: undefined,
														}}
													>
														{iss.isRestock
															? `+${Math.abs(iss.quantity)}`
															: iss.quantity}
													</TableCell>
													<TableCell
														align="right"
														sx={{
															fontSize: 11,
															color: "success.main",
														}}
													>
														{iss.totalCost != null
															? `$${Number(iss.totalCost).toLocaleString(undefined, { minimumFractionDigits: 2 })}`
															: "—"}
													</TableCell>
													<TableCell
														sx={{
															fontSize: 11,
															color: "text.secondary",
														}}
													>
														{iss.comments || "—"}
													</TableCell>
												</TableRow>
											);
										})}
									</TableBody>
								</Table>
							</TableContainer>
						</Box>
					)}
				</Box>
			</DialogContent>

			<DialogActions sx={{ px: 3, pb: 2 }}>
				{initialData && onPrint && (
					<PATPrintButton
						onClick={() => onPrint(initialData)}
						tooltipText="Print"
					/>
				)}
				{!viewOnly && (
					<PATSaveButton
						onClick={handleSubmit(onSubmit)}
						tooltipText={initialData ? "Save Changes" : "Add Item"}
						disabled={!isValid}
					/>
				)}
				<PATCloseButton onClick={onClose} tooltipText="Close" />
			</DialogActions>
		</Dialog>
	);
};

export default AddEditDispensableItemDialog;
